import React, { useRef, useState, useCallback, useEffect } from 'react'
import { EditorState } from './text-editor.types'
import { useChangesHistory } from './text-editor.history'
import {
  resetEditor,
  getTextBeforeCaret,
  shouldConsiderEmpty,
  determineEditorState,
  pasteContentToEditor,
  convertHtmlToFormattedText,
  copySelectedContentToClipboard,
  putCaretAtEndOfEditor,
} from './ops'
import { useEditorAPI } from './text-editor.api'
import { roomSearchApi } from '$comp/_app/components/chat/room'
import { editLastMessage } from '$logic/opened-room'
import { SendFieldAction } from '../../send-field.type'
import { IS_IOS, isPhoneOrTablet } from '$func/environmentInfo'

interface PropTypes {
  action?: SendFieldAction
  mentionIsDisabled: boolean
  setHasMessage: (hasMessage: boolean) => unknown
  setMentionQuery: (query: string) => unknown
  onSubmit: () => unknown
  onPasteFile: (file: File) => unknown
  onContentChange: (text: string) => void
  resetSendField: () => unknown
  resetSendFieldAction: () => unknown
}

export function useTextEditor({
  action,
  mentionIsDisabled,
  setHasMessage,
  setMentionQuery,
  onSubmit,
  onPasteFile,
  onContentChange,
  resetSendField,
  resetSendFieldAction,
}: PropTypes) {
  const EditorRef = useRef<HTMLDivElement>(null)
  const editorState = useRef<EditorState>('TEXT')

  const [editorIsEmpty, setEditorIsEmpty] = useState(true)
  // const { addToHistory, undo, redo } = useChangesHistory()

  const updateEditorState = useCallback(
    (contentChanged = true) => {
      const editorElement = EditorRef.current
      setEditorIsEmpty(shouldConsiderEmpty(editorElement))

      if (contentChanged) {
        // addToHistory(editorElement)
        onContentChange(convertHtmlToFormattedText(editorElement))
      }

      const newEditorState = determineEditorState(mentionIsDisabled)
      if (newEditorState === 'PRE_MENTION') setMentionQuery('')

      if (newEditorState === 'MENTION') {
        const textBeforeCaret = getTextBeforeCaret()
        const mentionQuery = textBeforeCaret.slice(
          textBeforeCaret.lastIndexOf('@') + 1,
        )

        setMentionQuery(mentionQuery)
      }

      if (newEditorState === 'TEXT') {
        if (
          editorState.current === 'PRE_MENTION' ||
          editorState.current === 'MENTION'
        )
          setMentionQuery(undefined)
      }

      editorState.current = newEditorState
    },
    [onContentChange, mentionIsDisabled],
  )

  useEffect(() => {
    setHasMessage(!editorIsEmpty)
  }, [editorIsEmpty])

  useEditorAPI(EditorRef, updateEditorState)

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const { code, shiftKey, ctrlKey, metaKey } = e

      if (shouldConsiderEmpty(EditorRef.current))
        putCaretAtEndOfEditor(EditorRef.current)

      if (code === 'ArrowUp' && editorIsEmpty) {
        putCaretAtEndOfEditor(EditorRef.current)
        editLastMessage().then(() => {
          const editorEl = EditorRef.current
          if (editorEl) {
            editorEl.scrollTo?.(0, editorEl.scrollHeight)
            editorEl.focus()
          }
        })

        e.preventDefault()
        return
      }

      if (code === 'KeyF' && (ctrlKey || metaKey)) {
        roomSearchApi.handleSearch('')
        e.preventDefault()
        return
      }

      // if (code === 'KeyZ' && (metaKey || ctrlKey)) {
      //   if (!shiftKey) undo(EditorRef.current)
      //   else redo(EditorRef.current)
      //
      //   updateEditorState(false)
      //   e.preventDefault()
      //   return
      // }

      if (code === 'Enter' && !shiftKey) {
        if (IS_IOS) {
          document.execCommand('lineBreak')
          return
        }

        // Send Message
        onSubmit()
        e.preventDefault()
      }
    },
    [editorIsEmpty, onSubmit],
  )

  const onCut = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    copySelectedContentToClipboard(true)
    updateEditorState()

    e.preventDefault()
  }, [])

  const onCopy = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    copySelectedContentToClipboard()
    e.preventDefault()
  }, [])

  const onPaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    if (e.clipboardData.files.length > 0) onPasteFile(e.clipboardData.files[0])

    const pastedPlainText = e.clipboardData.getData('text/plain')
    if (pastedPlainText) {
      pasteContentToEditor(pastedPlainText, EditorRef.current)
      updateEditorState()
    }

    e.preventDefault()
  }, [])

  useEffect(() => {
    const editorElem = EditorRef.current
    if (!isPhoneOrTablet) editorElem.focus()

    if (shouldConsiderEmpty(editorElem)) {
      resetEditor(editorElem)
      setEditorIsEmpty(true)
    }
  }, [])

  return {
    EditorRef,
    editorState,
    editorIsEmpty,
    updateEditorState,
    onKeyDown,
    onCut,
    onCopy,
    onPaste,
  }
}
